import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Coins, Send, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { ClapIcon } from "../../lib/icons";

// Live chat panel inside the home player overlay. Extracted from
// FeedPlayerOverlay.jsx in Iter-Feb 2026 to keep the overlay file
// under control. Pure prop-driven — owns no state.
const FeedLiveChatPanel = ({
  user,
  homeChat,
  liveChatTtlDays,
  newHomeChat,
  setNewHomeChat,
  submittingHomeChat,
  handleApplaud,
  handleSubmitHomeChat,
  handleDeleteHomeChat,
  handleClosePlayer,
}) => {
  const canAffordApplaud = !user || user.credits >= 0.50;
  const canAffordHomeChat = !user || user.credits >= 0.10;

  return (
    <div className="w-full bg-gray-900/80 rounded-lg flex flex-col flex-1 min-h-0" data-testid="home-live-chat-panel">
      <div className="p-2 border-b border-gray-800 flex items-center justify-between gap-2 flex-nowrap">
        <h4 className="text-white font-semibold text-xs flex items-center gap-1.5 min-w-0 truncate">
          <MessageCircle className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
          <span className="shrink-0">Live Chat</span>
          <span className="text-gray-500 font-normal text-[10px] truncate" data-testid="live-chat-ttl-label">
            · {homeChat.length} msg · disappears in {liveChatTtlDays}d
          </span>
        </h4>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleApplaud}
            disabled={user && !canAffordApplaud}
            data-testid="feed-livechat-applaud"
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
              canAffordApplaud
                ? 'bg-pink-500/20 hover:bg-pink-500/30 text-pink-400'
                : 'bg-amber-500/15 text-amber-400 cursor-not-allowed'
            }`}
            title={!canAffordApplaud ? "Need 0.50 credits — top up" : "Applaud this video (0.50 credits)"}
          >
            {canAffordApplaud ? <ClapIcon className="h-4 w-4" /> : <Coins className="h-3 w-3" />}
          </button>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 px-2 py-1">
        {homeChat.length > 0 ? (
          <div className="space-y-1">
            {homeChat.slice().reverse().map((msg) => (
              <div key={msg.id} className="flex items-start gap-2 text-xs group">
                <Link
                  to={`/${msg.username}`}
                  className="text-emerald-400 font-medium hover:underline shrink-0"
                  onClick={handleClosePlayer}
                >
                  {msg.username}:
                </Link>
                <span className="text-gray-300 flex-1">{msg.content}</span>
                {user?.is_admin && (
                  <button
                    onClick={() => handleDeleteHomeChat(msg.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 p-0.5 rounded transition-opacity"
                    title="Delete message (Admin)"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-xs text-center py-2">No messages yet. Start the conversation!</p>
        )}
      </ScrollArea>

      <div className="p-2 border-t border-gray-800">
        <form onSubmit={handleSubmitHomeChat} className="flex gap-2">
          <Input
            value={newHomeChat}
            onChange={(e) => setNewHomeChat(e.target.value)}
            placeholder={user && !canAffordHomeChat ? "Need 0.10 credits to chat" : "Say something..."}
            className="flex-1 bg-gray-800 border-gray-700 text-white text-xs h-7"
            disabled={submittingHomeChat || (user && !canAffordHomeChat)}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!newHomeChat.trim() || submittingHomeChat || (user && !canAffordHomeChat)}
            title={user && !canAffordHomeChat ? "Need 0.10 credits — top up" : "Post chat (0.10 credits)"}
            data-testid="feed-livechat-submit"
            className={`h-7 px-2 ${user && !canAffordHomeChat ? 'bg-amber-600/40 hover:bg-amber-600/60' : 'bg-yellow-600 hover:bg-yellow-500'}`}
          >
            {user && !canAffordHomeChat ? <Coins className="h-3 w-3" /> : <Send className="h-3 w-3" />}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default FeedLiveChatPanel;
